import type { EntityUpgradesList } from '../../../types/game-types';
import type { CurrencyState } from '../../zustand/useCurrencyStore';

export const inspirationReducer = (
  inspiration: CurrencyState['inspiration'],
  amountGenerated: number,
  upgrades: EntityUpgradesList,
): CurrencyState['inspiration'] => {
  // Don't generate inspiration while the inspiration is active
  if (inspiration.remainingActive > 0 || inspiration.remainingCooldown > 0) {
    return inspiration;
  }

  const newInspiration = { ...inspiration };

  const maxInspirationAmount = 100;

  // Get max multiplier from bard upgrades
  // Initial is 1.5, each purchase adds 0.5, max is 3.0
  const maxInspirationMultiplier = upgrades.bard.luteSolo.multiplier;

  const inspirationIncrement = 0.5;

  const nextInspirationAmount = newInspiration.amount + amountGenerated;
  const hasReachedMaxMultiplier = inspiration.multiplier >= maxInspirationMultiplier;

  if (hasReachedMaxMultiplier) {
    newInspiration.amount = 0;
    newInspiration.multiplier = maxInspirationMultiplier;
  } else if (nextInspirationAmount > maxInspirationAmount) {
    // Handle increments that exceed the max limit by converting excess into multipliers
    let remainingAmount = nextInspirationAmount;

    // Keep incrementing multiplier until remaining amount is within limit or max multiplier is reached
    while (remainingAmount > maxInspirationAmount && newInspiration.multiplier < maxInspirationMultiplier) {
      newInspiration.multiplier += inspirationIncrement;
      remainingAmount -= maxInspirationAmount;
    }

    // If we've reached max multiplier, set amount to 0
    // Otherwise, set amount to the remaining value (which should now be <= maxInspirationAmount)
    if (newInspiration.multiplier >= maxInspirationMultiplier) {
      newInspiration.amount = 0;
      newInspiration.multiplier = maxInspirationMultiplier;
    } else {
      newInspiration.amount = remainingAmount;
    }
  } else {
    newInspiration.amount = nextInspirationAmount;
  }

  return newInspiration;
};
